import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";

import { Bar } from "react-chartjs-2";
import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components required for this bar chart.
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


const AC_COLOUR = "#277a3e";
const DC_COLOUR = "#f5a000";


// Creates an array containing every hour from 0 to 23.
const hours = Array.from({ length: 24 }, (_, hour) => hour);


// Formats each hour into a readable time such as 08:00.
function formatHour(hour) {
  return `${String(hour).padStart(2, "0")}:00`;
}


function UtilizationByHourChart({
  data = [],
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Find the utilization value for a particular charger type and hour.
  const findValue = (type, hour) => {
    const row = data.find(
      (item) =>
        item.chargerType === type &&
        Number(item.hour) === hour
    );

    return row ? Number(row.value) : 0;
  };


  // Create datasets based on the selected charger type.
  const datasets = [];


  if (showAC) {
    datasets.push({
      label: "AC",
      data: hours.map((hour) => findValue("AC", hour)),
      backgroundColor: AC_COLOUR,
      borderColor: AC_COLOUR,
      borderWidth: 1
    });
  }


  if (showDC) {
    datasets.push({
      label: "DC",
      data: hours.map((hour) => findValue("DC", hour)),
      backgroundColor: DC_COLOUR,
      borderColor: DC_COLOUR,
      borderWidth: 1
    });
  }


  const chartData = {
    labels: hours.map(formatHour),
    datasets
  };


  // Configure the chart axes, legend and percentage tooltips.
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Time of Day"
        },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90
        }
      },
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        title: {
          display: true,
          text: "Utilization (%)"
        },
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "right"
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${Number(
              context.raw
            ).toFixed(2)}%`
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>Average Utilization Over Time of Day</h2>

          <p>
            Percentage of available outlet time occupied during each
            hour of the day.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title="Average Utilization Over Time of Day"
          enableZoom={false}
        />
      </div>


      {/* Render the hourly charger utilization bar chart */}
      <div className="chart-canvas-container">
        <Bar
          ref={chartRef}
          data={chartData}
          options={options}
        />
      </div>
    </article>
  );
}


export default UtilizationByHourChart;